import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { dataApi } from '../../services/api';
import { FilterButtons } from '../../components/ui/filter-buttons';
import { toast, Toaster } from 'sonner';
import PageHeaderActions from '../../components/PageHeaderActions';

// shadcn UI components
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';

// Custom components
import UsersTable from '../../components/UsersTable';
import UserForm from '../overlays/UserForm';
import LicenceBilling from '../../components/settings/LicenceBilling';
import QuoteFormSettings from '../../components/settings/QuoteFormSettings';
import ShopLineSettings from '../../components/settings/ShopLineSettings';
import PanelQuoteInsurerMapping from '../../components/settings/PanelQuoteInsurerMapping';
import HistorySettings from '../../components/settings/HistorySettings';
import { useLicence } from '../../context/LicenceContext';

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // History — every change in the team, and the recycle bin — is for admins:
  // the team owner, or anyone with the admin label (as LicenceBilling decides).
  const { isAdmin } = useLicence();
  
  // Initialize active tab from URL path
  // Kept in step with the effect below, so a link straight to /settings/integrations
  // opens on that tab rather than flashing General first.
  const getInitialTab = () => {
    const path = location.pathname;
    if (path.includes('/users')) return 'users';
    if (path.includes('/integrations')) return 'integrations';
    if (path.includes('/line-items')) return 'line-items';
    if (path.includes('/history')) return 'history';
    return 'general';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [panelQuoteSettings, setPanelQuoteSettings] = useState({
    enabled: false,
    username: '',
    password: '',
    hasPassword: false,
    damageCategory: 'Accident Damage'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState({});

  const getStatusBadge = (enabled) => {
    return (
      <Badge variant={enabled ? "default" : "secondary"}>
        {enabled ? 'Enabled' : 'Disabled'}
      </Badge>
    );
  };

  const loadPanelQuoteSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dataApi.panelQuoteSettingsGet();
      const settings = {
        enabled: response?.enabled || false,
        username: response?.username || '',
        password: '',
        hasPassword: response?.hasPassword || false,
        damageCategory: response?.damageCategory || 'Accident Damage'
      };
      setPanelQuoteSettings(settings);
      setOriginalSettings(settings);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error loading Panel Quote settings:', error);
      toast.error('Failed to load Panel Quote settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPanelQuoteSettings();
  }, [loadPanelQuoteSettings]);

  // Sync tab state with URL changes (browser back/forward)
  useEffect(() => {
    const path = location.pathname;
    let newTab = 'general';
    if (path.includes('/users')) newTab = 'users';
    else if (path.includes('/integrations')) newTab = 'integrations';
    else if (path.includes('/line-items')) newTab = 'line-items';
    // A non-admin who types /settings/history lands on General.
    else if (path.includes('/history') && isAdmin) newTab = 'history';
    
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.pathname, activeTab, isAdmin]);

  const handlePanelQuoteSettingsChange = (field, value) => {
    const newSettings = {
      ...panelQuoteSettings,
      [field]: value
    };
    setPanelQuoteSettings(newSettings);
    
    // Check if settings have been modified
    const isModified = newSettings.enabled !== originalSettings.enabled ||
                      newSettings.username !== originalSettings.username ||
                      newSettings.damageCategory !== originalSettings.damageCategory ||
                      (newSettings.password && newSettings.password !== '');
    setHasUnsavedChanges(isModified);
    
    // Clear test result when settings change
    if (testResult) {
      setTestResult(null);
    }
  };

  const savePanelQuoteSettings = async () => {
    try {
      setSaving(true);
      setTestResult(null);

      // Validate required fields
      if (panelQuoteSettings.enabled) {
        if (!panelQuoteSettings.username) {
          toast.error('Username is required when Panel Quote is enabled');
          return;
        }
        if (!panelQuoteSettings.password && !panelQuoteSettings.hasPassword) {
          toast.error('Password is required when Panel Quote is enabled');
          return;
        }
      }

      const response = await dataApi.panelQuoteSettingsSave({
        enabled: panelQuoteSettings.enabled,
        username: panelQuoteSettings.username,
        password: panelQuoteSettings.password?.trim() || undefined,
        damageCategory: panelQuoteSettings.damageCategory
      });

      toast.success('Panel Quote settings saved successfully');
      const updatedSettings = {
        ...panelQuoteSettings,
        password: '',
        enabled: response.enabled !== undefined ? response.enabled : panelQuoteSettings.enabled,
        hasPassword: response.hasPassword !== undefined ? response.hasPassword : panelQuoteSettings.hasPassword
      };
      setPanelQuoteSettings(updatedSettings);
      setOriginalSettings(updatedSettings);
      setHasUnsavedChanges(false);
      
      if (panelQuoteSettings.enabled) {
        setTimeout(() => {
          testPanelQuoteConnection();
        }, 500);
      }
    } catch (error) {
      console.error('Error saving Panel Quote settings:', error);
      toast.error('Failed to save Panel Quote settings');
    } finally {
      setSaving(false);
    }
  };

  const testPanelQuoteConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);

      // Use dataApi for function invocation
      const response = await dataApi.panelQuoteTestConnection();

      // Check for success - the function returns { success: true/false, message: '...' }
      const success = response?.success === true;
      const errorMessage = response?.error || response?.message || 'Connection failed';
      
      setTestResult({
        success,
        message: success ? 'Connection successful' : errorMessage
      });

      if (success) {
        toast.success('Connection successful');
      } else {
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error testing Panel Quote connection:', error);
      const errorMessage = error?.message || 'Connection test failed';
      setTestResult({
        success: false,
        message: errorMessage
      });
      toast.error(errorMessage);
    } finally {
      setTesting(false);
    }
  };

  const tabs = [
    { value: 'general', label: 'General' },
    { value: 'line-items', label: 'Quote Line Items' },
    { value: 'users', label: 'Users' },
    { value: 'integrations', label: 'Integrations' },
    ...(isAdmin ? [{ value: 'history', label: 'History' }] : []),
  ];

  // The line sheet is a wide table — a section column, an insurer column and
  // three number columns — so it takes the whole width and the Quick Navigation
  // panel stands down. There is nothing to navigate to on that tab anyway: it is
  // one thing, not a page of sections.
  // History is one long list with its own filters, so it takes the width too.
  const showQuickNav = activeTab !== 'line-items' && activeTab !== 'history';

  if (loading) {
    return (
      <div className="flex flex-col h-full p-6 gap-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-9 w-48" />
        </div>
        <div className="flex flex-col items-center justify-center py-12">
          <Skeleton className="h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-32 mt-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6 gap-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold">Settings</h1>
        <PageHeaderActions />
      </div>

      {/* Tab Navigation */}
      <Card>
        <CardContent className="p-4">
          <FilterButtons
            options={tabs}
            selectedValue={activeTab}
            onChange={(tab) => {
              setActiveTab(tab);
              // Update URL when tab changes
              navigate(`/settings/${tab}`, { replace: true });
            }}
          />
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className={`grid grid-cols-1 gap-6 flex-1 min-h-0 ${showQuickNav ? 'xl:grid-cols-4' : ''}`}>
        {/* Left Navigation Panel */}
        {showQuickNav && (
        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle>Quick Navigation</CardTitle>
          </CardHeader>
          <CardContent>
            {activeTab === 'general' && (
              <div className="space-y-2">
                <a href="#licence-billing" className="block text-sm text-primary hover:underline">
                  Licence &amp; Billing
                </a>
                <a href="#quote-form" className="block text-sm text-primary hover:underline">
                  Quote Request Form
                </a>
                <div className="text-sm text-muted-foreground">
                  Organisation Details <span className="text-xs">(Coming Soon)</span>
                </div>
              </div>
            )}
            {activeTab === 'users' && (
              <div className="space-y-2">
                <a href="#current-users" className="block text-sm text-primary hover:underline">
                  Current Users
                </a>
                <a href="#old-users" className="block text-sm text-primary hover:underline">
                  Old Users
                </a>
              </div>
            )}
            {activeTab === 'integrations' && (
              <div className="space-y-2">
                <a href="#panel-quote" className="block text-sm text-primary hover:underline">
                  Panel Quote Integration
                </a>
                <div className="text-sm text-muted-foreground">
                  IAG Integration <span className="text-xs">(Coming Soon)</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  PNET Integration <span className="text-xs">(Coming Soon)</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  PartsTrader Integration <span className="text-xs">(Coming Soon)</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Right Content Panel. Scrollable on every tab EXCEPT the line
            sheet, which owns its own scrolling: the table body scrolls under a
            frozen header, and a scrollbar on the panel as well would put two
            side by side and let the column headings drift away from the
            columns. */}
        <Card className={`flex flex-col min-h-0 ${showQuickNav ? 'xl:col-span-3' : ''}`}>
          <CardContent className={`flex-1 min-h-0 pt-8 ${
            activeTab === 'line-items' ? 'flex flex-col overflow-hidden pb-4' : 'overflow-y-auto'
          }`}>
            {activeTab === 'history' && isAdmin && <HistorySettings />}

            {activeTab === 'line-items' && (
              <div className="flex flex-col gap-4 min-h-0 flex-1">
                {/* Frozen with everything else above the table. */}
                <div className="shrink-0">
                  <h2 className="text-xl font-semibold">Quote Line Items</h2>
                  <p className="text-muted-foreground mt-1">
                    Line items the insurer guides do not have, but that you still bill
                  </p>
                </div>
                <ShopLineSettings />
              </div>
            )}

            {activeTab === 'general' && (
              <div className="space-y-8">
                {/* Licence & Billing. Sits under the organisation's settings
                    rather than the user menu — the plan belongs to the shop,
                    not to whoever is signed in. */}
                <div id="licence-billing" className="space-y-4">
                  <h2 className="text-xl font-semibold">Licence &amp; Billing</h2>
                  <p className="text-muted-foreground">
                    Your plan, what it includes, and your invoices
                  </p>
                  <LicenceBilling />
                </div>

                {/* The public intake form: the link customers use, whether it
                    is open, and the shop's branding on it. */}
                <div id="quote-form" className="space-y-4 mt-12">
                  <h2 className="text-xl font-semibold">Quote Request Form</h2>
                  <QuoteFormSettings />
                </div>

                <div id="organisation" className="space-y-4 mt-12 opacity-60">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-semibold">Organisation Details</h2>
                    <Badge variant="secondary">Coming Soon</Badge>
                  </div>
                  <p className="text-muted-foreground">Manage organisation details and preferences</p>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-8">
                {/* Current Users Section */}
                <div id="current-users" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Current Users</h2>
                    <Button onClick={() => setUserFormOpen(true)}>
                      Add User
                    </Button>
                  </div>
                  <p className="text-muted-foreground">Active users in your organization</p>
                  <UsersTable isActive={true} />
                </div>

                {/* Old Users Section */}
                <div id="old-users" className="space-y-4 mt-12">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Old Users</h2>
                  </div>
                  <p className="text-muted-foreground">Inactive users in your organization</p>
                  <UsersTable isActive={false} />
                </div>
              </div>
            )}

            {activeTab === 'integrations' && (
              <div className="space-y-8">
                {/* Panel Quote Integration Section */}
                <div id="panel-quote" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-semibold">Panel Quote Integration</h2>
                      {getStatusBadge(panelQuoteSettings.enabled)}
                    </div>
                    {hasUnsavedChanges && (
                      <Button
                        onClick={savePanelQuoteSettings}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    )}
                  </div>
                  <p className="text-muted-foreground">Import jobs and data from Panel Quote system</p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="panel-quote-enabled"
                          checked={panelQuoteSettings.enabled}
                          onCheckedChange={(checked) => handlePanelQuoteSettingsChange('enabled', checked)}
                        />
                        <Label htmlFor="panel-quote-enabled">Enable Panel Quote Integration</Label>
                      </div>

                      {panelQuoteSettings.enabled && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="panel-quote-username">Username</Label>
                            <Input
                              id="panel-quote-username"
                              type="text"
                              value={panelQuoteSettings.username}
                              onChange={(e) => handlePanelQuoteSettingsChange('username', e.target.value)}
                              placeholder="Enter Panel Quote username"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="panel-quote-password">Password</Label>
                            <Input
                              id="panel-quote-password"
                              type="password"
                              value={panelQuoteSettings.password}
                              onChange={(e) => handlePanelQuoteSettingsChange('password', e.target.value)}
                              placeholder={panelQuoteSettings.hasPassword ? "Enter new password to change" : "Enter Panel Quote password"}
                            />
                            {panelQuoteSettings.hasPassword && (
                              <p className="text-xs text-muted-foreground">
                                Password is already configured. Enter a new password to change it.
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="panel-quote-damage-category">Damage Photo Category</Label>
                            <Input
                              id="panel-quote-damage-category"
                              type="text"
                              value={panelQuoteSettings.damageCategory}
                              onChange={(e) => handlePanelQuoteSettingsChange('damageCategory', e.target.value)}
                              placeholder="Accident Damage"
                            />
                            <p className="text-xs text-muted-foreground">
                              Panel Quote photo category containing accident damage photos. Only photos in this category are assessed for damage by AI.
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {panelQuoteSettings.enabled && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Connection Test</Label>
                          <Button
                            variant="outline"
                            onClick={testPanelQuoteConnection}
                            disabled={testing || hasUnsavedChanges || !panelQuoteSettings.enabled || !originalSettings.username || !originalSettings.hasPassword}
                            className="w-full"
                          >
                            {testing ? 'Testing...' : 'Test Connection'}
                          </Button>

                          {testResult && (
                            <div className={`p-3 rounded-md border ${
                              testResult.success
                                ? 'bg-green-50 border-green-200 text-green-800'
                                : 'bg-red-50 border-red-200 text-red-800'
                            }`}>
                              <div className="flex items-center gap-2">
                                <span>{testResult.success ? '✅' : '❌'}</span>
                                <span className="text-sm">{testResult.message}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Credentials have to be saved before the address book can be
                      fetched, so the mapping only appears once they are. */}
                  <PanelQuoteInsurerMapping
                    enabled={panelQuoteSettings.enabled && !!originalSettings.username && !!originalSettings.hasPassword}
                  />
                </div>

                {/* Future integrations - placeholder sections */}
                <div id="iag" className="space-y-4 opacity-60">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-semibold">IAG Integration</h2>
                    <Badge variant="secondary">Coming Soon</Badge>
                  </div>
                  <p className="text-muted-foreground">Connect with IAG systems for enhanced quote processing</p>
                  <div className="text-center py-8">
                    <h3 className="font-semibold mb-2">Coming Soon</h3>
                    <p className="text-muted-foreground">IAG integration is under development</p>
                  </div>
                </div>

                <div id="pnet" className="space-y-4 opacity-60">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-semibold">PNET Integration</h2>
                    <Badge variant="secondary">Coming Soon</Badge>
                  </div>
                  <p className="text-muted-foreground">Connect with PNET for parts and pricing information</p>
                  <div className="text-center py-8">
                    <h3 className="font-semibold mb-2">Coming Soon</h3>
                    <p className="text-muted-foreground">PNET integration is under development</p>
                  </div>
                </div>

                <div id="partstrader" className="space-y-4 opacity-60">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-semibold">PartsTrader Integration</h2>
                    <Badge variant="secondary">Coming Soon</Badge>
                  </div>
                  <p className="text-muted-foreground">Integrate with PartsTrader for parts sourcing and pricing</p>
                  <div className="text-center py-8">
                    <h3 className="font-semibold mb-2">Coming Soon</h3>
                    <p className="text-muted-foreground">PartsTrader integration is under development</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* User Form Dialog */}
      <UserForm
        isOpen={userFormOpen}
        onClose={() => setUserFormOpen(false)}
        onUserCreated={() => setUserFormOpen(false)}
      />
      
      <Toaster position="top-right" closeButton />
    </div>
  );
};

export default Settings;
